import { create } from 'zustand';

/**
 * Where the palette anchors and how it lays out:
 *  - 'point'    — anchored to a click point (canvas Cmd/Ctrl-click). Input on top,
 *                 results/chips below. Quick-create chips only.
 *  - 'bottom'   — anchored just above the bottom dock. Input pinned to the BOTTOM,
 *                 results + the full create-menu list stack UPWARD above it.
 *  - 'topRight' — anchored below a system-bar icon (top-right of the screen).
 *                 Input on top, growing down, right-clamped to the viewport.
 *                 Does not require a worldPos (like 'bottom').
 *  - 'center'   — the "expanded" Quick Actions surface (Cmd+K). Wider,
 *                 vertically anchored ~15vh from the top and horizontally
 *                 centered, independent of screenPos. Does not require a
 *                 worldPos (like 'bottom'/'topRight').
 */
export type SlimPalettePlacement = 'point' | 'bottom' | 'topRight' | 'center';

/** A screenshot attached to the palette (cmd+drag region-select flow) — see
 * regionScreenshotService.ts. When set (either status), the palette skips
 * entity/create matching entirely and becomes a pure "ask about this image"
 * input. On the fast native Electron capture path the drag hook captures
 * first, then opens with 'ready'. On the slower DOM fallback path it opens
 * with 'loading' immediately (so a multi-second capture never looks like a
 * hang) and setPendingImageReady() patches that to 'ready' once the capture
 * resolves. */
export type SlimPalettePendingImage =
  | { status: 'loading' }
  | { status: 'ready'; dataUrl: string; width: number; height: number };

interface OpenOptions {
  /** Screen-space anchor point (clientX/Y). For 'bottom', this is the dock button center. */
  screenPos: { x: number; y: number };
  /**
   * World-space point (canvas coordinates) for entity/window placement. Optional:
   * omit (or pass null) when launching from the dock, where windows use the
   * layout's default placement rather than a world point.
   */
  worldPos?: { x: number; y: number } | null;
  /** Anchor + layout mode. Defaults to 'point'. */
  placement?: SlimPalettePlacement;
  /** Attached screenshot to ask about — see SlimPalettePendingImage. */
  pendingImage?: SlimPalettePendingImage | null;
}

interface SlimCreatePaletteStore {
  isOpen: boolean;
  screenPos: { x: number; y: number } | null;
  worldPos: { x: number; y: number } | null;
  placement: SlimPalettePlacement;
  pendingImage: SlimPalettePendingImage | null;
  open: (opts: OpenOptions) => void;
  close: () => void;
  /** Patches a still-loading pendingImage to 'ready' once the cmd+drag
   * capture resolves. No-op if the palette was closed, or reopened for
   * something else, in the meantime. */
  setPendingImageReady: (dataUrl: string, width: number, height: number) => void;
}

export const useSlimCreatePaletteStore = create<SlimCreatePaletteStore>((set, get) => ({
  isOpen: false,
  screenPos: null,
  worldPos: null,
  placement: 'point',
  pendingImage: null,
  open: ({ screenPos, worldPos = null, placement = 'point', pendingImage = null }) =>
    set({ isOpen: true, screenPos, worldPos, placement, pendingImage }),
  close: () => set({ isOpen: false, screenPos: null, worldPos: null, placement: 'point', pendingImage: null }),
  setPendingImageReady: (dataUrl, width, height) => {
    const { isOpen, pendingImage } = get();
    if (!isOpen || pendingImage?.status !== 'loading') return;
    set({ pendingImage: { status: 'ready', dataUrl, width, height } });
  },
}));
